import React, { useState } from 'react';
import { Card, Statistic, Row, Col, DatePicker, Empty, Spin, Rate, Avatar, List } from 'antd';
import { useEmployees } from '../../../hooks/useEmployee';
import { getImg } from '../../../Api/getImg';
import dayjs from 'dayjs';
import { UserOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;

const PerformanceTab = ({ specialistId }) => {
    const [dateRange, setDateRange] = useState(null);
    const { useDoctorPerformance } = useEmployees();

    const { data, isLoading } = useDoctorPerformance(
        specialistId,
        dateRange?.[0]?.toISOString(),
        dateRange?.[1]?.toISOString()
    );

    if (isLoading) return <div className="p-10 text-center"><Spin tip="Loading Performance..." /></div>;

    return (
        <div className="p-4">
            <div className="mb-6 flex justify-between items-center bg-gray-50 p-4 rounded-lg">
                <span className="font-semibold text-gray-600">Filter Performance Period:</span>
                <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    allowClear={false}
                />
            </div>

            {!data ? (
                <Empty description="No performance data found for this period" />
            ) : (
                <>
                    {/* Statistics */}
                    <Row gutter={[16, 16]} className="mb-6">
                        <Col span={8}>
                            <Card bordered={false} className="shadow-sm bg-[#F7FCFD]">
                                <Statistic
                                    title="Total Appointments"
                                    value={data.total}
                                    valueStyle={{ color: '#298E9F' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card bordered={false} className="shadow-sm">
                                <Statistic
                                    title="Completed"
                                    value={data.totalCompleted}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card bordered={false} className="shadow-sm">
                                <Statistic
                                    title="Canceled"
                                    value={data.totalCanceled}
                                    valueStyle={{ color: '#ff4d4f' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Reviews */}
                    <Card
                        title={
                            <span className="font-semibold text-gray-700">
                                Patient Reviews ({data.reviews?.length || 0})
                            </span>
                        }
                        bordered={false}
                        className="shadow-sm"
                    >
                        {data.reviews?.length === 0 || !data.reviews ? (
                            <Empty description="No reviews yet" />
                        ) : (
                            <List
                                dataSource={data.reviews}
                                renderItem={(review) => (
                                    <List.Item className="border-b last:border-0">
                                        <div className="flex gap-4 w-full">
                                            {/* Avatar */}
                                            <Avatar
                                                src={review.userPhoto ? getImg(review.userPhoto) : null}
                                                icon={!review.userPhoto && <UserOutlined />}
                                                size={48}
                                                className="flex-shrink-0"
                                            />

                                            {/* Content */}
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="font-semibold text-gray-800 mb-1">
                                                            {review.userName}
                                                        </p>
                                                        <Rate disabled value={review.rating} className="text-sm" />
                                                    </div>
                                                    <span className="text-xs text-gray-400">
                                                        {dayjs(review.createdAt).format('DD MMM YYYY')}
                                                    </span>
                                                </div>
                                                <p className="text-gray-600 mt-2 text-sm">{review.text}</p>
                                            </div>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </>
            )}
        </div>
    );
};

export default PerformanceTab;